import cx from 'classnames';
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useId,
    useState,
} from 'react';

import type {ColorScheme} from '../../../types/variants';
import {Text} from '../Text/Text';
import {InputLabel} from '../InputLabel/InputLabel';
import {ThemeConsumer} from '../../../themes/ThemeContext';
import {useAutosizeTextarea} from '../../../hooks/useAutoResizeTextArea';

import styles from './TextArea.module.scss';

type ResizeVariant = 'resizeNone' | 'resizeBoth' | 'resizeV' | 'resizeH';

export const TextArea = forwardRef<
    {
        focus: () => void;
    },
    Omit<React.ComponentPropsWithRef<'textarea'>, 'disabled'> & {
        colorScheme?: ColorScheme;
        errorText?: string;
        hasError?: boolean;
        id?: string;
        isDisabled?: boolean;
        label: string;
        isLabelHidden?: boolean;
        isAutoResize?: boolean;
        autoResizeMaxHeight?: number;
        name?: string;
        onValueChange?: (value: string) => void;
        value?: string;
        resize?: ResizeVariant;
        rows?: number;
        textareaClassname?: string;
    }
>(
    (
        {
            className,
            textareaClassname,
            colorScheme,
            errorText,
            hasError,
            id,
            isDisabled,
            isAutoResize = false,
            autoResizeMaxHeight,
            label,
            isLabelHidden = false,
            name,
            onChange: inheritedOnChange,
            onValueChange,
            value,
            resize = 'resizeNone',
            rows = 4,
            ...props
        },
        ref,
    ) => {
        const defaultId = useId();
        const effectiveId = id ?? defaultId;

        const [textareaElement, setTextareaElement] =
            useState<HTMLTextAreaElement | null>(null);

        useAutosizeTextarea({
            textareaElement,
            textareaValue: value,
            maxHeight: autoResizeMaxHeight,
            enabled: isAutoResize,
        });

        const onChange = useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (onValueChange) {
                    onValueChange(e.currentTarget.value);
                }
                if (inheritedOnChange) {
                    inheritedOnChange(e);
                }
            },
            [inheritedOnChange, onValueChange],
        );

        const labelProps = {text: label, id: effectiveId};

        useImperativeHandle(
            ref,
            () => ({
                focus: () => {
                    if (textareaElement) {
                        textareaElement.focus();
                    }
                },
                value: textareaElement?.value,
            }),
            [textareaElement],
        );

        return (
            <ThemeConsumer>
                {({colorScheme: defaultColorScheme}) => (
                    <label
                        className={cx(
                            styles.wrap,
                            styles[colorScheme ?? defaultColorScheme],
                            {
                                [styles.disabled]: isDisabled,
                                [styles.error]: hasError && !isDisabled,
                            },
                            className,
                        )}
                    >
                        <InputLabel
                            isLabelHidden={isLabelHidden}
                            {...labelProps}
                        />

                        <textarea
                            rows={rows}
                            id={effectiveId}
                            name={name}
                            onChange={onChange}
                            ref={setTextareaElement}
                            value={value}
                            className={cx(
                                styles.textarea,
                                textareaClassname,
                                styles[resize],
                            )}
                            disabled={isDisabled}
                            {...props}
                        />
                        {hasError && errorText && (
                            <Text
                                className={cx('mt-2', styles.errorText)}
                                variant="danger"
                                role="alert"
                            >
                                {errorText}
                            </Text>
                        )}
                    </label>
                )}
            </ThemeConsumer>
        );
    },
);

TextArea.displayName = 'TextArea';

export type TextAreaProps = React.ComponentProps<typeof TextArea>;
